import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  addDoc,
  deleteDoc
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../firebase/config';
import dayjs from 'dayjs';

export const useBookingUtils = (currentUser, userProfile, setSuccess, setError) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch bookings from Firestore
  useEffect(() => {
    if (!currentUser) return;

    const q = query(
      collection(db, 'bookings'),
      where('salonId', '==', currentUser.uid),
      orderBy('date', 'asc')
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const bookingsData = [];
      querySnapshot.forEach((doc) => {
        bookingsData.push({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date?.toDate ? doc.data().date.toDate() : new Date(doc.data().date)
        });
      });
      bookingsData.sort((a, b) => new Date(b.date) - new Date(a.date));
      setBookings(bookingsData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Utility functions
  const getBookingsByStatus = (status) => {
    return bookings.filter(booking => booking.status === status);
  };

  const getUpcomingBookings = () => {
    const now = new Date();
    return bookings.filter(booking => 
      booking.status === 'confirmed' && booking.date >= now
    );
  };

  const getPastBookings = () => {
    const now = new Date();
    return bookings.filter(booking => booking.date < now);
  };

  const getTodayBookings = () => {
    const today = new Date();
    const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
    
    return bookings.filter(booking => 
      booking.date >= startOfDay && booking.date < endOfDay
    );
  };

  const getThisMonthBookings = () => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    return bookings.filter(booking => 
      booking.date >= startOfMonth && booking.date <= endOfMonth
    );
  };

  const getMonthlyRevenue = () => {
    const thisMonthBookings = getThisMonthBookings();
    return thisMonthBookings
      .filter(booking => booking.status === 'confirmed')
      .reduce((total, booking) => {
        const service = userProfile.services?.find(s => s.name === booking.service);
        return total + (service ? parseFloat(service.price) : 0);
      }, 0);
  };

  const getCompletionRate = () => {
    const totalBookings = bookings.length;
    const completedBookings = bookings.filter(b => b.status === 'confirmed').length;
    return totalBookings > 0 ? Math.round((completedBookings / totalBookings) * 100) : 0;
  };

  // Filter bookings based on search term
  const filterBookings = (bookingsList, searchTerm) => {
    if (!searchTerm) return bookingsList;
    
    return bookingsList.filter(booking =>
      booking.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.clientEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.clientPhone?.includes(searchTerm) ||
      booking.service.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Update booking status
  const updateBookingStatus = async (bookingId, status) => {
    try {
      const bookingRef = doc(db, 'bookings', bookingId);
      
      // Récupérer les données de la réservation avant mise à jour
      const booking = bookings.find(b => b.id === bookingId);
      
      await updateDoc(bookingRef, {
        status: status,
        updatedAt: new Date()
      });

      // Envoyer l'email de confirmation/annulation au client
      try {
        if (booking && booking.clientEmail && status === 'confirmed') {
          const sendClientEmail = httpsCallable(functions, 'sendClientBookingEmail');
          await sendClientEmail({
            clientEmail: booking.clientEmail,
            bookingStatus: 'confirmed',
            bookingData: {
              clientName: booking.clientName,
              service: booking.service,
              date: dayjs(booking.date).format('DD/MM/YYYY'),
              time: dayjs(booking.date).format('HH:mm'),
              salonName: userProfile?.businessName || userProfile?.salonName || 'Votre salon',
              salonPhone: userProfile?.phone,
              salonEmail: userProfile?.email
            }
          });
          console.log(`✅ Status update email sent to client: ${status}`);
        }
      } catch (emailError) {
        console.error('❌ Error sending status update email:', emailError);
        // Ne pas faire échouer la mise à jour si l'email échoue
      }

      setSuccess(`Réservation ${status === 'confirmed' ? 'confirmée' : 'annulée'}!`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error updating booking:', error);
      setError('Erreur lors de la mise à jour de la réservation');
    }
  };

  // Save booking (create or update)
  const saveBooking = async (bookingForm, editingBooking) => {
    if (!bookingForm.clientName || !bookingForm.service || !bookingForm.date || !bookingForm.time) {
      setError('Veuillez remplir tous les champs obligatoires');
      return false;
    }

    try {
      const bookingDate = bookingForm.date
        .hour(bookingForm.time.hour())
        .minute(bookingForm.time.minute())
        .toDate();

      const bookingData = {
        salonId: currentUser.uid,
        salonName: userProfile.salonName,
        clientName: bookingForm.clientName,
        clientEmail: bookingForm.clientEmail,
        clientPhone: bookingForm.clientPhone,
        service: bookingForm.service,
        date: bookingDate,
        notes: bookingForm.notes,
        status: 'confirmed',
        updatedAt: new Date(),
        confirmationSent: false,
        reminderSent: false
      };

      if (editingBooking) {
        const bookingRef = doc(db, 'bookings', editingBooking.id);
        await updateDoc(bookingRef, bookingData);
        setSuccess('Réservation mise à jour!');
      } else {
        bookingData.createdAt = new Date();
        const docRef = await addDoc(collection(db, 'bookings'), bookingData);
        
        // Send confirmation email if client email is provided
        if (bookingForm.clientEmail) {
          try {
            const sendBookingConfirmation = httpsCallable(functions, 'sendBookingConfirmation');
            await sendBookingConfirmation({
              clientEmail: bookingForm.clientEmail,
              bookingData: {
                clientName: bookingForm.clientName,
                service: bookingForm.service,
                date: bookingDate.toLocaleDateString('fr-FR'),
                time: bookingDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
                salonName: userProfile.salonName || userProfile.businessName,
                salonPhone: userProfile.phone,
                salonAddress: userProfile.address ? `${userProfile.address}, ${userProfile.city}` : null
              }
            });
            
            await updateDoc(docRef, { confirmationSent: true });
            console.log('✅ Confirmation email sent for manual booking');
          } catch (emailError) {
            console.error('❌ Error sending confirmation email:', emailError);
          }
        }
        
        setSuccess('Réservation ajoutée!');
      }

      setTimeout(() => setSuccess(''), 3000);
      return true;
    } catch (error) {
      console.error('Error saving booking:', error);
      setError('Erreur lors de la sauvegarde de la réservation');
      return false;
    }
  };

  // Delete booking
  const deleteBooking = async (bookingId) => {
    try {
      await deleteDoc(doc(db, 'bookings', bookingId));
      setSuccess('Réservation supprimée!');
      setTimeout(() => setSuccess(''), 3000);
      return true;
    } catch (error) {
      console.error('Error deleting booking:', error);
      setError('Erreur lors de la suppression de la réservation');
      return false;
    }
  };

  return {
    bookings,
    loading,
    getBookingsByStatus,
    getUpcomingBookings,
    getPastBookings,
    getTodayBookings,
    getThisMonthBookings,
    getMonthlyRevenue,
    getCompletionRate,
    filterBookings,
    updateBookingStatus,
    saveBooking,
    deleteBooking
  };
};